/**
 * The icons the consoles draw, by what they mean rather than what they look
 * like.
 *
 * The rail used Unicode glyphs (◱ ⌁ ⚖ ⇌), each from a different font at a
 * different weight, most of them one pixel wide at 0.78rem. These are one
 * family — Lucide, ISC — at one stroke width, so a row of them reads as a set.
 *
 * Named by job: a screen asks for `disputes`, not for a pair of scales. When
 * the drawing for a job changes, it changes here and nowhere else, and two
 * screens doing the same job cannot drift onto two different pictures.
 */
import {
  ArrowLeftRight,
  Banknote,
  Bell,
  BookText,
  Building,
  ChartColumn,
  Check,
  ChevronRight,
  CircleUser,
  ClipboardCheck,
  Coins,
  Compass,
  Copy,
  Database,
  Diff,
  Flag,
  Info,
  LayoutDashboard,
  Link,
  Link2,
  LogOut,
  Menu,
  Monitor,
  Moon,
  Network,
  OctagonAlert,
  Pencil,
  RotateCcw,
  Scale,
  ScrollText,
  Settings,
  ShieldAlert,
  Signpost,
  SlidersHorizontal,
  Stamp,
  Store,
  Sun,
  Tags,
  TriangleAlert,
  UserCog,
  Users,
  UsersRound,
  Webhook,
  Workflow,
  X,
  type LucideIcon,
} from 'lucide-react';

const ICONS = {
  // Navigation.
  dashboard: LayoutDashboard,
  reports: ChartColumn,
  brands: Store,
  setup: SlidersHorizontal,
  affiliates: Users,
  // §12.3 — an affiliate's own recruits, one level down from them (the
  // affiliate console's "Sub-affiliates" nav item).
  network: Network,
  disputes: Scale,
  approvals: ClipboardCheck,
  reconciliation: ArrowLeftRight,
  fraud: ShieldAlert,
  adjustments: Diff,
  team: UserCog,
  settings: Settings,
  audit: Link2,
  // The operator's Compliance tab (creative sign-off + audit trail) — a
  // stamp for a certification, not AMS's own hash-chained "Audit chain"
  // (`audit`, a literal chain link there); reusing `audit` beside Fraud's
  // shield read as "Links".
  compliance: Stamp,
  consumers: Workflow,
  operators: Building,
  'reference-data': Database,
  'feature-flags': Flag,
  links: Link,
  sources: Tags,
  // What the affiliate's own tracker is told when one of their players converts.
  postbacks: Webhook,
  terms: ScrollText,
  commission: Coins,
  statements: BookText,
  invoices: Banknote,
  history: RotateCcw,
  players: UsersRound,
  discover: Compass,
  account: CircleUser,

  // A value copied by clicking it, rather than a separate button beside it.
  copy: Copy,
  'copy.done': Check,

  // Renaming a label in place, where the label itself is the row's text.
  rename: Pencil,

  // Chrome.
  notifications: Bell,
  menu: Menu,
  close: X,
  open: ChevronRight,
  'sign-out': LogOut,
  // The rail's intro-tour button.
  tour: Signpost,
  'theme.system': Monitor,
  'theme.light': Sun,
  'theme.dark': Moon,

  // Callout tones.
  'tone.note': Info,
  'tone.caution': TriangleAlert,
  'tone.error': OctagonAlert,
} as const satisfies Record<string, LucideIcon>;

export type IconName = keyof typeof ICONS;

/**
 * Decorative by default: every icon here sits beside a word that says the same
 * thing, and a screen reader that announced both would say it twice. An icon
 * that stands alone gets its name from the button it is in.
 */
export function Icon({
  name,
  size = 16,
  className,
}: {
  name: IconName;
  size?: number;
  className?: string;
}) {
  const Drawing = ICONS[name];
  return (
    <Drawing
      size={size}
      strokeWidth={1.75}
      aria-hidden="true"
      focusable="false"
      className={className ? `icon ${className}` : 'icon'}
    />
  );
}
